import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCompetitions } from '../../services/competitionService';
import { getAgeGroups } from '../../services/ageGroupService';
import { getSportsCategories } from '../../services/competitionService';
import { getReferees } from '../../services/refereeService';
import { getActiveSportsYear, getSportsYears } from '../../services/sportsYearService';
import { Competition, AgeGroup, SportsCategory, Referee } from '../../types/models';
import { Search, Plus, Calendar, ChevronLeft, ChevronRight, Database, RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const ITEMS_PER_PAGE = 15;

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const AdminCompetitionList: React.FC = () => {
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [categories, setCategories] = useState<SportsCategory[]>([]);
  const [referees, setReferees] = useState<Referee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedVerifier, setSelectedVerifier] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        loadData();
        setupRealtimeSubscription();
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [competitionsData, ageGroupsData, categoriesData, refereesData, sportsYears, activeYear] = await Promise.all([
        getCompetitions(),
        getAgeGroups(),
        getSportsCategories(),
        getReferees(),
        getSportsYears().catch(() => []),
        getActiveSportsYear().catch(() => null),
      ]);

      const yearsFromData = Array.from(
        new Set(competitionsData.map(c => c.competition_year).filter(Boolean))
      ) as number[];
      const mergedYears = Array.from(
        new Set([...sportsYears.map(y => y.year), ...yearsFromData])
      ).sort((a, b) => b - a);
      setAvailableYears(mergedYears);

      if (selectedYear === 'All' && activeYear && mergedYears.includes(activeYear)) {
        setSelectedYear(activeYear.toString());
      }
      
      // Fetch results with verified_by information
      const { data: results, error: resultsError } = await supabase
        .from('competition_results')
        .select('result_id, event_id, verified_by');
      
      if (resultsError) {
        console.error('Error fetching results:', resultsError);
      }
      
      // Enhance competitions with verification info
      const enhancedCompetitions = competitionsData.map(comp => {
        const verifiedResults = results?.filter(r => r.event_id === comp.event_id) || [];
        const mobileVerified = verifiedResults.some(r => r.verified_by === 'mobile_results');
        
        return {
          ...comp,
          verifiers: mobileVerified ? ['mobile_results'] : null
        };
      });
      
      setCompetitions(enhancedCompetitions);
      setAgeGroups(ageGroupsData);
      setCategories(categoriesData);
      setReferees(refereesData);
      setError(null);
      setRefreshing(false);
    } catch (error: any) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    // Subscribe to events table changes
    const eventsSubscription = supabase
      .channel('events-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'events' 
      }, () => {
        console.log('Events table changed, refreshing data...');
        loadData();
      })
      .subscribe();

    // Subscribe to competition_results table changes
    const resultsSubscription = supabase
      .channel('results-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'competition_results' 
      }, () => {
        console.log('Results table changed, refreshing data...');
        loadData();
      })
      .subscribe();

    // Subscribe to team_results table changes
    const teamResultsSubscription = supabase
      .channel('team-results-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'team_results' 
      }, () => {
        console.log('Team results table changed, refreshing data...');
        loadData();
      })
      .subscribe();

    // Cleanup subscriptions on component unmount
    return () => {
      eventsSubscription.unsubscribe();
      resultsSubscription.unsubscribe();
      teamResultsSubscription.unsubscribe();
    };
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGender, selectedAgeGroup, selectedStatus, selectedCategory, selectedVerifier, selectedYear, sortConfig]);

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  const getFilteredAndSortedCompetitions = () => {
    let filteredCompetitions = competitions.filter(competition => {
      const matchesSearch = 
        competition.event_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        competition.category?.category_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (competition.reference_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (competition.verification_code || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesGender = selectedGender === 'All' || competition.gender_category === selectedGender;
      const matchesAgeGroup = selectedAgeGroup === 'All' || competition.age_group_id === selectedAgeGroup;
      const matchesStatus = selectedStatus === 'All' || competition.status === selectedStatus;
      const matchesCategory = selectedCategory === 'All' || competition.category_id === selectedCategory;
      
      // Filter by verifier
      const matchesVerifier = selectedVerifier === 'All' ||
        (selectedVerifier === 'Mobile' && competition.verifiers && competition.verifiers.includes('mobile_results')) ||
        (selectedVerifier === 'Manual' && (!competition.verifiers || !competition.verifiers.includes('mobile_results')));

      const matchesYear = selectedYear === 'All' || competition.competition_year?.toString() === selectedYear;

      return matchesSearch && matchesGender && matchesAgeGroup && matchesStatus && matchesCategory && matchesVerifier && matchesYear;
    });

    // Apply sorting if configured
    if (sortConfig !== null) {
      filteredCompetitions = [...filteredCompetitions].sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        let aValue = getNestedProperty(a, sortConfig.key);
        let bValue = getNestedProperty(b, sortConfig.key);
        
        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Number comparison
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }

    return filteredCompetitions;
  };

  const filteredCompetitions = getFilteredAndSortedCompetitions();
  const totalPages = Math.ceil(filteredCompetitions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCompetitions = filteredCompetitions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleManualRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('competitions.admin.title')}</h1>
        <div className="mt-3 md:mt-0 flex space-x-3">
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <Link
            to="/competitions/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('competitions.actions.addCompetition')}
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('competitions.admin.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allGenders')}</option>
                <option value="M">{t('competitions.filters.male')}</option>
                <option value="F">{t('competitions.filters.female')}</option>
                <option value="A">{t('competitions.filters.all')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedAgeGroup}
                onChange={(e) => setSelectedAgeGroup(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allAgeGroups')}</option>
                {ageGroups.map(group => (
                  <option key={group.age_group_id} value={group.age_group_id}>
                    {group.group_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allStatuses')}</option>
                <option value="not_started">{t('competitions.status.not_started')}</option>
                <option value="in_progress">{t('competitions.status.in_progress')}</option>
                <option value="completed">{t('competitions.status.completed')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allCategories')}</option>
                {categories.map(category => (
                  <option key={category.category_id} value={category.category_id}>
                    {category.category_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedVerifier}
                onChange={(e) => setSelectedVerifier(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('competitions.filters.allVerifiers')}</option>
                <option value="Mobile">{t('competitions.filters.mobileResults')}</option>
                <option value="Manual">{t('competitions.filters.manualEntry')}</option>
              </select>
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">All years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y.toString()}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredCompetitions.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('competitions.noCompetitions.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedGender !== 'All' || selectedAgeGroup !== 'All' || selectedStatus !== 'All' || selectedCategory !== 'All' || selectedVerifier !== 'All' || selectedYear !== 'All'
                ? t('competitions.noCompetitions.description.withFilters')
                : t('competitions.noCompetitions.description.noFilters')}
            </p>
            <div className="mt-6">
              <Link
                to="/competitions/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('competitions.actions.addCompetition')}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('event_name')}
                    >
                      {t('competitions.table.eventName')}
                      {getSortIcon('event_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('reference_number')}
                    >
                      {t('competitions.table.referenceNumber')}
                      {getSortIcon('reference_number')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('verification_code')}
                    >
                      {t('competitions.table.verificationCode')}
                      {getSortIcon('verification_code')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('category.category_name')}
                    >
                      {t('competitions.table.category')}
                      {getSortIcon('category.category_name')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('age_group_id')}
                    >
                      {t('competitions.table.ageGroup')}
                      {getSortIcon('age_group_id')}
                    </th>
                    <th 
                      scope="col" 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('gender_category')}
                    >
                      {t('competitions.table.gender')}
                      {getSortIcon('gender_category')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('status')}
                    >
                      {t('competitions.table.status')}
                      {getSortIcon('status')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => requestSort('competition_year')}
                    >
                      Year
                      {getSortIcon('competition_year')}
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {t('competitions.table.verifiedBy')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCompetitions.map((competition) => (
                    <tr key={competition.event_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/competitions/${competition.event_id}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {competition.event_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {competition.reference_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {competition.verification_code}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {competition.category?.category_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {ageGroups.find(g => g.age_group_id === competition.age_group_id)?.group_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.gender_category === 'M' ? 'bg-blue-100 text-blue-800' :
                          competition.gender_category === 'F' ? 'bg-pink-100 text-pink-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {competition.gender_category === 'M' ? t('competitions.filters.male') :
                           competition.gender_category === 'F' ? t('competitions.filters.female') : 
                           t('competitions.filters.all')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                          competition.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {t(`competitions.status.${competition.status}`)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {competition.competition_year ?? '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {competition.verifiers && competition.verifiers.includes('mobile_results') ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {t('competitions.filters.mobileResults')}
                          </span>
                        ) : competition.status === 'completed' ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {t('competitions.filters.manualEntry')}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredCompetitions.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredCompetitions.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCompetitionList;